<template>
  <div>
    <h3 class="section-subtitle mb-4">Enrol Learner to Syllabus</h3>

    <v-form @submit.prevent="submitEnrolment">
      <v-row>
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="form.instructorId"
            :items="activeInstructors"
            item-value="adi_id"
            label="Select Instructor *"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-account-tie"
            :error-messages="instructorErrors"
            @change="onInstructorChange"
            @blur="$v.form.instructorId.$touch()"
          >
            <template v-slot:item="{ item }">
              <strong>A{{ item.adi_id }}</strong
              >&nbsp;–&nbsp;{{ item.title }} {{ item.fname }} {{ item.lname }}
            </template>
            <template v-slot:selection="{ item }">
              A{{ item.adi_id }} – {{ item.title }} {{ item.fname }}
              {{ item.lname }}
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="form.learnerId"
            :items="learnersForInstructor"
            item-value="stu_id"
            label="Select Learner *"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-account"
            :disabled="!form.instructorId"
            :loading="learnersLoading"
            :error-messages="learnerErrors"
            @blur="$v.form.learnerId.$touch()"
          >
            <template v-slot:item="{ item }">
              <strong>L{{ item.stu_id }}</strong
              >&nbsp;–&nbsp;{{ item.title }} {{ item.fname }} {{ item.lname }}
            </template>
            <template v-slot:selection="{ item }">
              L{{ item.stu_id }} – {{ item.title }} {{ item.fname }}
              {{ item.lname }}
            </template>
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-title>
                  {{
                    form.instructorId
                      ? "No learners found for this instructor."
                      : "Select an instructor first."
                  }}
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="form.syllabusId"
            :items="syllabuses"
            item-text="name"
            item-value="id"
            label="Select Syllabus *"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-book-open-variant"
            :error-messages="syllabusErrors"
            @blur="$v.form.syllabusId.$touch()"
          >
            <template v-slot:item="{ item }">
              <span>
                {{ item.name }}
                <v-chip x-small color="success" dark class="ml-1">{{
                  item.version
                }}</v-chip>
              </span>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-btn color="primary" type="submit" :loading="submitting">
            <v-icon left>mdi-account-plus</v-icon>
            Enrol Learner
          </v-btn>
          <v-btn text class="ml-2" @click="resetForm" :disabled="submitting">
            Clear
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import {
  M_OU,
  GET_ACTIVE_INSTRUCTORS,
  GET_ADI_LEARNERS,
  SAVE_NEW_SYLLABUS_ENROLMENT,
} from "@/store/mutation-list";

export default {
  name: "EnrolLearnerForm",

  props: {
    syllabuses: { type: Array, default: () => [] },
  },

  validations: {
    form: {
      instructorId: { required },
      learnerId: { required },
      syllabusId: { required },
    },
  },

  data() {
    return {
      form: {
        instructorId: null,
        learnerId: null,
        syllabusId: null,
      },
      learnersForInstructor: [],
      learnersLoading: false,
      submitting: false,
    };
  },

  computed: {
    ...mapGetters({
      activeInstructors: `${M_OU}${GET_ACTIVE_INSTRUCTORS}`,
    }),

    instructorErrors() {
      const errors = [];
      if (!this.$v.form.instructorId.$dirty) return errors;
      if (!this.$v.form.instructorId.required)
        errors.push("Please select an instructor.");
      return errors;
    },

    learnerErrors() {
      const errors = [];
      if (!this.$v.form.learnerId.$dirty) return errors;
      if (!this.$v.form.learnerId.required)
        errors.push("Please select a learner.");
      return errors;
    },

    syllabusErrors() {
      const errors = [];
      if (!this.$v.form.syllabusId.$dirty) return errors;
      if (!this.$v.form.syllabusId.required)
        errors.push("Please select a syllabus.");
      return errors;
    },
  },

  methods: {
    ...mapActions({
      getAdiLearners: `${M_OU}${GET_ADI_LEARNERS}`,
      enrollLearner: `${M_OU}${SAVE_NEW_SYLLABUS_ENROLMENT}`,
    }),

    showAlert(type, title, message) {
      this.$fire({ title, text: message, type, timer: 3000 });
    },

    onInstructorChange(adiId) {
      this.form.learnerId = null;
      this.learnersForInstructor = [];
      if (!adiId) return;

      this.learnersLoading = true;
      this.getAdiLearners({ id: adiId })
        .then((res) => {
          this.learnersForInstructor = res || [];
        })
        .finally(() => {
          this.learnersLoading = false;
        });
    },

    resetForm() {
      this.form = { instructorId: null, learnerId: null, syllabusId: null };
      this.learnersForInstructor = [];
      this.$v.$reset();
    },

    submitEnrolment() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fill in all required fields."
        );
        return;
      }
      this.submitting = true;
      this.enrollLearner({
        syllabus_id: this.form.syllabusId,
        learner_id: this.form.learnerId,
        instructor_id: this.form.instructorId,
      })
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Enrolled",
              "Learner enrolled successfully."
            );
            this.resetForm();
            this.$emit("enrolled");
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to enrol learner. Please try again."
            );
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.section-subtitle {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 4px 0 0;
}
</style>
